import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    titlePlanPaused: string;
    titleAccountLocked: string;
    detailsPlanPausedWithPermission: (showResumePlanDialog: VoidFunction) => string | React.ReactNode;
    detailsPlanPausedNoPermission: (ownerEmail: string) => string | React.ReactNode;
    detailsAdobeWithPermission: ({ launcherId, trackIntercomOpened }: {
        launcherId: string;
        trackIntercomOpened: VoidFunction;
    }) => string | React.ReactNode;
    detailsAdobeNoPermission: string;
    detailsNoPermission: (ownerEmail: string) => string | React.ReactNode;
    detailsReduceUsage: string;
    detailsUpdatePayment: () => string | React.ReactNode;
    detailsContactSupport: ({ launcherId, trackIntercomOpened }: {
        launcherId: string;
        trackIntercomOpened: VoidFunction;
    }) => string | React.ReactNode;
};
export type Deps = {
    StyledA: any;
    UniversalLink: any;
    USAGE_PAGE_URL: any;
    BILLING_PAGE_URL: any;
};
export default {
    DEFINE(_messages: Text, { StyledA, UniversalLink, USAGE_PAGE_URL, BILLING_PAGE_URL }: Deps): void {
        return Object.defineProperties(this, {
            /** Title when plan is paused  */
            titlePlanPaused: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "AccountLockBanner.titlePlanPaused?c=3i45jo01ouk6e", defaultMessage: "This account is unavailable while frozen." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title when account is locked */
            titleAccountLocked: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "AccountLockBanner.titleAccountLocked?c=2fh0t0347nknp", defaultMessage: "Account Locked" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for billing managers when plan is paused */
            detailsPlanPausedWithPermission: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (showResumePlanDialog: VoidFunction) => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsPlanPausedWithPermission?c=2ay3cng1ws3iw", defaultMessage: "<jsx>To regain full access,  <StyledA>reactivate the account</StyledA> .</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), StyledA: (__children__: React.ReactNode) => <StyledA onClick={showResumePlanDialog}>{React.Children.toArray(__children__)}</StyledA> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for non-billing users when plan is paused */
            detailsPlanPausedNoPermission: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (ownerEmail: string) => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsPlanPausedNoPermission?c=2el9o1cyix76g", defaultMessage: "<jsx>Contact account owner ( <StyledA>{ownerEmail}</StyledA> ) for assistance.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), StyledA: (__children__: React.ReactNode) => <StyledA href={`mailto:${ownerEmail}`} target="_blank">{React.Children.toArray(__children__)}</StyledA>, ownerEmail: ownerEmail }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for Adobe-managed accounts with billing permissions */
            detailsAdobeWithPermission: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ launcherId, trackIntercomOpened }: {
                        launcherId: string;
                        trackIntercomOpened: VoidFunction;
                    }) => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsAdobeWithPermission?c=3sbbrjtnk5uh1", defaultMessage: "<jsx><UniversalLink>Review usage</UniversalLink> or  <StyledA>contact support</StyledA>  for assistance.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), UniversalLink: (__children__: React.ReactNode) => <UniversalLink href={USAGE_PAGE_URL}>{React.Children.toArray(__children__)}</UniversalLink>, StyledA: (__children__: React.ReactNode) => <StyledA id={launcherId} onClick={trackIntercomOpened} rel="noreferrer">{React.Children.toArray(__children__)}</StyledA> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for Adobe-managed accounts without billing permissions */
            detailsAdobeNoPermission: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsAdobeNoPermission?c=1c4sxknq7xt21", defaultMessage: "Contact your admin for assistance." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for non-billing users */
            detailsNoPermission: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (ownerEmail: string) => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsNoPermission?c=22o4hp5c5tbp7", defaultMessage: "<jsx>Contact the account owner ( <UniversalLink>{ownerEmail}</UniversalLink> ) for assistance.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), UniversalLink: (__children__: React.ReactNode) => <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">{React.Children.toArray(__children__)}</UniversalLink>, ownerEmail: ownerEmail }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message when usage needs to be reduced */
            detailsReduceUsage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsReduceUsage?c=wy3g1ptv0dj4", defaultMessage: "Reduce usage or upgrade to unlock" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message when payment needs to be updated */
            detailsUpdatePayment: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => () => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsUpdatePayment?c=2htrq9pl4fgb9", defaultMessage: "<jsx><UniversalLink>Update payment details</UniversalLink>  to resolve overdue balance.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), UniversalLink: (__children__: React.ReactNode) => <UniversalLink href={BILLING_PAGE_URL}>{React.Children.toArray(__children__)}</UniversalLink> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Default fallback message */
            detailsContactSupport: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ launcherId, trackIntercomOpened }: {
                        launcherId: string;
                        trackIntercomOpened: VoidFunction;
                    }) => __intl__.intl().formatMessage({ id: "AccountLockBanner.detailsContactSupport?c=197iabd13b5f3", defaultMessage: "<jsx><StyledA>Contact Support</StyledA>  for assistance.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), StyledA: (__children__: React.ReactNode) => <StyledA id={launcherId} onClick={trackIntercomOpened} rel="noreferrer">{React.Children.toArray(__children__)}</StyledA> }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps: Deps): void;
};
